// Client feedback — NPS (ideas track, idea 41, schema 10).
//
// After a completed meeting or a closed claim the CRM PREPARES (never sends) a short message with a one-question survey
// link: «How likely are you to recommend me to a friend? 0–10» plus an optional comment. The page is served by the
// platform (packages/platform/src/nps.ts) and holds only the advisor's name — never the client's name or any data.
// The answer comes back through the platform; the CRM records it on the client's card (one changeset) and:
//   - 9–10 (promoter): a polite «would you leave a Google review?» DRAFT with the review link set in Settings;
//   - 7–8  (passive):  nothing else;
//   - 0–6  (detractor): a high-priority call-back task for today.
// NPS = % promoters − % detractors over the answers in a period (the analytics chart).
import { stableId } from './ids.ts';
import type { Changeset, Put, WorkspaceView } from './changeset.ts';
import { DomainError, clean, created, opt, put, requirePerson, timeline, touched, type Ctx } from './cmd.ts';
import { getSetting } from './services.ts';
import type { NpsCategory, NpsSurvey, NpsTrigger } from './schema.ts';

/** a survey link is valid this long */
export const NPS_VALID_DAYS = 30;
/** a client is not asked again within this many days (a meeting and a claim the same month = one question) */
export const NPS_COOLDOWN_DAYS = 90;
export const NPS_REVIEW_URL_KEY = 'nps.reviewUrl';

export const npsCategory = (score: number): NpsCategory => (score >= 9 ? 'promoter' : score >= 7 ? 'passive' : 'detractor');
/** 128 bits, URL-safe (the link is the only key to the survey page) */
export function newNpsToken(): string {
  const b = new Uint8Array(16); globalThis.crypto.getRandomValues(b);
  return [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
}
export const isNpsToken = (s: unknown): s is string => typeof s === 'string' && /^[0-9a-f]{32}$/.test(s);
/** the survey link for a token (the platform's public address) */
export const npsLink = (platformUrl: string, token: string) => `${platformUrl.replace(/\/+$/, '')}/nps/${token}`;

/** the review link the advisor set (https only), or null */
export function npsReviewUrl(v: WorkspaceView): string | null {
  const u = clean(getSetting<string>(v, NPS_REVIEW_URL_KEY, ''));
  return /^https:\/\/[^\s]+$/.test(u) ? u : null;
}

/** the survey already prepared for this meeting / claim, if any */
export function surveyFor(v: WorkspaceView, refTable: string, refId: string): NpsSurvey | undefined {
  return v.where('nps_surveys', 'refId', refId).find((s) => s.refTable === refTable);
}
/** may we suggest a survey for this person now? (not archived, has a way to receive it, not asked recently) */
export function npsSuggested(v: WorkspaceView, personId: string, nowIso: string): { ok: boolean; reason?: 'archived' | 'recent' | 'no_contact' } {
  const p = v.get('persons', personId);
  if (!p || p.archivedAt) return { ok: false, reason: 'archived' };
  if (!p.primaryEmail && !p.primaryPhone && !p.whatsapp) return { ok: false, reason: 'no_contact' };
  const since = new Date(Date.parse(nowIso) - NPS_COOLDOWN_DAYS * 864e5).toISOString();
  if (v.where('nps_surveys', 'personId', personId).some((s) => s.status !== 'withdrawn' && s.preparedAt >= since)) return { ok: false, reason: 'recent' };
  return { ok: true };
}

export interface PrepareNpsInput { personId: string; trigger: NpsTrigger; refTable?: string | null; refId?: string | null; lang?: string | null; advisorName?: string | null; token?: string }
/** prepare a survey link for a client (nothing is sent: the advisor reviews the message and sends it himself) */
export function prepareNpsSurvey(v: WorkspaceView, input: PrepareNpsInput, ctx: Ctx): Changeset {
  const p = requirePerson(v, input.personId);
  if (p.archivedAt) throw new DomainError('nps.archived');
  if (input.trigger === 'meeting') {
    const m = input.refId ? v.get('meetings', input.refId) : undefined;
    if (!m || m.personId !== p.id) throw new DomainError('nps.ref');
    if (m.status !== 'held') throw new DomainError('nps.meeting_not_held');
  }
  if (input.trigger === 'claim') {
    const c = input.refId ? v.get('claims', input.refId) : undefined;
    if (!c || c.personId !== p.id) throw new DomainError('nps.ref');
    if (!['paid', 'closed', 'declined'].includes(c.status)) throw new DomainError('nps.claim_not_closed');
  }
  if (input.refId && input.refTable) {
    const prev = surveyFor(v, input.refTable, input.refId);
    if (prev && prev.status !== 'withdrawn') throw new DomainError('nps.duplicate');
  }
  const token = input.token ?? newNpsToken();
  if (!isNpsToken(token)) throw new DomainError('nps.token');
  const row = created<'nps_surveys'>(ctx, {
    personId: p.id, trigger: input.trigger, refTable: input.refTable ?? null, refId: input.refId ?? null, token,
    lang: input.lang === 'en' ? 'en' : 'el', advisorName: opt(input.advisorName)?.slice(0, 80) ?? null,
    status: 'prepared', preparedAt: ctx.now, expiresAt: new Date(Date.parse(ctx.now) + NPS_VALID_DAYS * 864e5).toISOString(),
    score: null, comment: null, answeredAt: null, category: null, taskId: null, reviewAskedAt: null,
  });
  return { action: 'nps.prepare', entity: 'nps_surveys', entityId: row.id, summary: p.displayName, puts: [put('nps_surveys', row), timeline(ctx, p.id, 'message', 'timeline.nps_prepared', null, { table: 'nps_surveys', id: row.id })], deletes: [] };
}

/** withdraw a prepared survey (kept, link stops working on the next sync) */
export function withdrawNpsSurvey(v: WorkspaceView, id: string, ctx: Ctx): Changeset {
  const s = v.get('nps_surveys', id);
  if (!s) throw new DomainError('nps.not_found');
  if (s.status !== 'prepared') throw new DomainError('nps.closed');
  const row = touched(ctx, s, { status: 'withdrawn' });
  return { action: 'nps.withdraw', entity: 'nps_surveys', entityId: id, puts: [put('nps_surveys', row)], deletes: [] };
}

export const npsCallbackTaskId = (surveyId: string) => stableId('nps_callback', surveyId);
export type NpsTaskTitle = (score: number) => string;
/** record the client's answer (from the platform): score, optional comment, category; 0–6 → call-back task */
export function recordNpsAnswer(v: WorkspaceView, input: { token: string; score: number; comment?: string | null; answeredAt?: string }, ctx: Ctx, title: NpsTaskTitle = (n) => `Call back — feedback ${n}/10`): Changeset {
  const s = v.where('nps_surveys', 'token', input.token)[0];
  if (!s) throw new DomainError('nps.not_found');
  if (s.status === 'answered') return { action: 'nps.answer', entity: 'nps_surveys', entityId: s.id, puts: [], deletes: [] }; // the same answer pulled twice
  const score = Number(input.score);
  if (!Number.isInteger(score) || score < 0 || score > 10) throw new DomainError('nps.score');
  const category = npsCategory(score);
  const comment = opt(input.comment)?.slice(0, 1000) ?? null;
  const puts: Put[] = [];
  let taskId: string | null = null;
  if (category === 'detractor') {
    taskId = npsCallbackTaskId(s.id);
    if (!v.get('tasks', taskId)) puts.push(put('tasks', created<'tasks'>(ctx, {
      id: taskId, kind: 'call', title: title(score).slice(0, 160), personId: s.personId, prospectId: null, caseId: null, policyId: null,
      dueDate: ctx.today, dueTime: null, durationMin: null, locationType: null, location: null, status: 'open', priority: 'high', notes: comment, origin: 'nps', data: { surveyId: s.id, score },
    })));
  }
  const row = touched(ctx, s, { status: 'answered', score, comment, category, answeredAt: input.answeredAt ?? ctx.now, taskId });
  puts.unshift(put('nps_surveys', row));
  puts.push(timeline(ctx, s.personId, 'review', 'timeline.nps_answered', `${score}/10${comment ? ` — ${comment}` : ''}`, { table: 'nps_surveys', id: s.id }));
  return { action: 'nps.answer', entity: 'nps_surveys', entityId: s.id, summary: `${score}/10`, puts, deletes: [] };
}
/** the advisor opened the Google-review draft for a promoter (recorded once, so it is not suggested again) */
export function markNpsReviewAsked(v: WorkspaceView, id: string, ctx: Ctx): Changeset {
  const s = v.get('nps_surveys', id);
  if (!s) throw new DomainError('nps.not_found');
  if (s.category !== 'promoter') throw new DomainError('nps.not_promoter');
  return { action: 'nps.review_asked', entity: 'nps_surveys', entityId: id, puts: s.reviewAskedAt ? [] : [put('nps_surveys', touched(ctx, s, { reviewAskedAt: ctx.now }))], deletes: [] };
}

// --- analytics -------------------------------------------------------------------------------------------------
export interface NpsSummary { answered: number; promoters: number; passives: number; detractors: number; nps: number | null; prepared: number; responseRate: number | null }
/** NPS over the answers dated [from, to] (days); unknown is not zero: no answers → nps null */
export function npsSummary(v: WorkspaceView, from: string, to: string): NpsSummary {
  const inRange = (iso: string | null | undefined) => !!iso && iso.slice(0, 10) >= from && iso.slice(0, 10) <= to;
  const rows = v.rows('nps_surveys');
  const ans = rows.filter((s) => s.status === 'answered' && s.score != null && inRange(s.answeredAt));
  const c = (k: NpsCategory) => ans.filter((s) => s.category === k).length;
  const prepared = rows.filter((s) => s.status !== 'withdrawn' && inRange(s.preparedAt)).length;
  const promoters = c('promoter'), passives = c('passive'), detractors = c('detractor');
  return { answered: ans.length, promoters, passives, detractors, nps: ans.length ? Math.round(((promoters - detractors) / ans.length) * 100) : null, prepared, responseRate: prepared ? Math.round((rows.filter((s) => s.status === 'answered' && inRange(s.preparedAt)).length / prepared) * 100) : null };
}
/** NPS per month for the last `months` months ending with `today`'s month (null where no answers) */
export function npsByMonth(v: WorkspaceView, today: string, months = 12): { month: string; answered: number; nps: number | null }[] {
  const out: { month: string; answered: number; nps: number | null }[] = [];
  const [y, m] = today.split('-').map(Number);
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(y!, m! - 1 - i, 1));
    const month = d.toISOString().slice(0, 7);
    const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
    const s = npsSummary(v, `${month}-01`, last);
    out.push({ month, answered: s.answered, nps: s.nps });
  }
  return out;
}
/** what the platform needs to serve the open surveys (token, language, advisor's name, end) — nothing about the client */
export function openSurveyLinks(v: WorkspaceView, nowIso: string): { token: string; lang: string; advisorName: string | null; expiresAt: string | null }[] {
  return v.rows('nps_surveys').filter((s) => s.status === 'prepared' && (!s.expiresAt || s.expiresAt > nowIso)).map((s) => ({ token: s.token, lang: s.lang ?? 'el', advisorName: s.advisorName ?? null, expiresAt: s.expiresAt ?? null }));
}
